// src/composables/useThemePreference.ts
import { ref, computed } from 'vue'
import type { ThemeInstance } from 'vuetify'

const STORAGE_KEY    = 'advisgo-theme-preference'
const THEME_LIGHT    = 'defaultTheme'
const THEME_DARK     = 'defaultThemeDark'

const manualPref     = ref<'auto' | 'dark' | 'light' | null>(null)
const effectiveDark  = ref(false)

let themeInstance: ThemeInstance | null = null
let initialized = false

function readPref() {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return v === 'dark' || v === 'light' || v === 'auto' ? v : null
  } catch {
    return null
  }
}

function writePref(v: 'auto' | 'dark' | 'light' | null) {
  try {
    if (v === null) localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, v)
  } catch {}
}

function browserPrefersDark() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  )
}

function applyTheme(dark: boolean) {
  if (!themeInstance) return
  themeInstance.global.name.value = dark ? THEME_DARK : THEME_LIGHT
}

function resolveDark() {
  if (manualPref.value === 'dark')  return true
  if (manualPref.value === 'light') return false
  return browserPrefersDark()
}

function setManual(v: 'dark' | 'light' | 'auto') {
  manualPref.value = v
  writePref(v)
  effectiveDark.value = resolveDark()
  applyTheme(effectiveDark.value)
}

function clearManual() {
  manualPref.value = null
  writePref(null)
  effectiveDark.value = resolveDark()
  applyTheme(effectiveDark.value)
}

function onSystemChange() {
  if (manualPref.value === 'auto' || manualPref.value === null) {
    effectiveDark.value = browserPrefersDark()
    applyTheme(effectiveDark.value)
  }
}

export function initThemeInstance(theme?: ThemeInstance) {
  if (theme) {
    themeInstance = theme
    applyTheme(effectiveDark.value)
  }
}

export function initIfNeeded(theme?: ThemeInstance) {
  if (initialized) {
    if (theme) initThemeInstance(theme)
    return
  }
  initialized = true

  manualPref.value    = readPref()
  effectiveDark.value = resolveDark()
  if (theme) initThemeInstance(theme)

  if (typeof window !== 'undefined') {
    window
      .matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', onSystemChange)

    window.addEventListener('storage', (e) => {
      if (e.key === STORAGE_KEY) {
        manualPref.value    = readPref()
        effectiveDark.value = resolveDark()
        applyTheme(effectiveDark.value)
      }
    })
  }
}

export function useThemePreference(theme?: ThemeInstance) {
  initIfNeeded(theme)

  // exposes a plain v-model target: 'auto' | 'light' | 'dark'
  const mode = computed<'auto' | 'light' | 'dark'>({
    get() {
      const v = manualPref.value
      return v === 'dark' || v === 'light' ? v : 'auto'
    },
    set(v) {
      v === 'auto' ? clearManual() : setManual(v)
    }
  })

  // whether the user has ever chosen light/dark manually
  const hasManual = computed(() => manualPref.value !== null)

  // current effective theme flag
  const isDark = computed(() => effectiveDark.value)

  return {
    mode,       // v-model target, no .value needed
    hasManual,  // boolean
    isDark,     // boolean
    initThemeInstance
  }
}
